import { useState, useEffect } from "react";
import { BookmarkPlus, BookmarkCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { savePrompt } from "@/services/savedPromptService";
import { toast } from "sonner";
import { analytics } from "@/services/analytics/analyticsService";
import { supabase } from "@/integrations/supabase/client";
import CollectionSelectorModal from "./CollectionSelectorModal";

interface SavePromptButtonProps {
  promptId: string;
  promptTitle: string;
  isLarge?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}

const SavePromptButton = ({ 
  promptId, 
  promptTitle, 
  isLarge = false,
  onClick
}: SavePromptButtonProps) => {
  const [saved, setSaved] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if prompt is saved
  useEffect(() => {
    const checkSavedStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }
      
      try {
        console.log(`Checking saved status for prompt ${promptId} and user ${user.id}`);
        // Check if the prompt is saved by querying the user_saved_prompts table
        const { data, error } = await supabase
          .from("user_saved_prompts")
          .select("id")
          .eq("user_id", user.id)
          .eq("prompt_id", promptId)
          .maybeSingle();
          
        if (error) {
          console.error("Error checking saved status:", error);
        } else {
          console.log(`Prompt ${promptId} saved status:`, !!data);
          setSaved(!!data);
        }
      } catch (error) {
        console.error("Error checking saved status:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSavedStatus();
  }, [promptId, user]);

  const handleSaveToLibrary = async (e: React.MouseEvent) => {
    // Stop propagation to prevent parent elements from handling the click
    e.stopPropagation();
    e.preventDefault();
    if (onClick) onClick(e);
    
    if (!user) {
      navigate("/sign-in");
      return;
    }
    
    try {
      setIsLoading(true);
      // Save the prompt and get the ID back
      const savedPromptId = await savePrompt(promptId);
      
      if (savedPromptId) {
        setSaved(true);
        toast.success("Prompt saved to your library");
        
        // Track the save event
        analytics.trackPromptSave(promptId, promptTitle);
        setShowSaveDialog(false);
      } else {
        // If we get null back, it means the prompt was removed (toggle functionality)
        setSaved(false);
        toast.success("Prompt removed from your library");
        setShowSaveDialog(false);
      }
    } catch (error) {
      console.error("Error saving prompt:", error);
      toast.error("Could not save prompt. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onClick) onClick(e);
    
    if (!user) {
      navigate("/sign-in");
      return;
    }

    // If already saved, just perform the save/remove toggle
    if (saved) {
      handleSaveToLibrary(e);
    } else {
      // If not saved, show the dialog
      setShowSaveDialog(true);
    }
  };

  const handleCollectionSuccess = () => {
    setSaved(true);
    setShowSaveDialog(false);
  };

  const buttonContent = isLarge ? (
    <>
      {saved ? (
        <>
          <BookmarkCheck className="h-5 w-5 mr-2" />
          Saved
        </>
      ) : (
        <>
          <BookmarkPlus className="h-5 w-5 mr-2" />
          Save
        </>
      )}
    </>
  ) : (
    saved ? <BookmarkCheck className="h-4 w-4" /> : <BookmarkPlus className="h-4 w-4" />
  );

  return (
    <>
      <Button
        variant={isLarge ? "ghost" : "ghost"}
        size={isLarge ? "default" : "icon"}
        className={`${isLarge ? "" : "h-6 w-6"} ${saved ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
        disabled={isLoading}
        onClick={handleButtonClick}
        title={saved ? "Saved" : "Save prompt"}
      >
        {buttonContent}
      </Button>

      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Prompt</DialogTitle>
            <DialogDescription>
              Save "{promptTitle}" for easy access later.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <Button 
              onClick={handleSaveToLibrary} 
              className="w-full"
              variant="outline"
            >
              <BookmarkPlus className="mr-2 h-4 w-4" />
              Save to Library
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>
            
            <CollectionSelectorModal
              promptId={promptId}
              promptTitle={promptTitle}
              onSuccess={handleCollectionSuccess}
              embedded={true}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SavePromptButton;
